import type { StatusData } from '../page';

function fmtMb(mb: number): string {
  if (mb === 0) return '0 MB';
  if (mb < 1024) return `${mb.toFixed(0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

function pct(used: number, quota: number): number {
  if (!quota) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

function barColor(p: number): string {
  if (p > 85) return 'bg-red-400';
  if (p > 60) return 'bg-amber-400';
  return 'bg-emerald-400';
}

interface Props { data: StatusData; }

export default function StorageStatusTab({ data }: Props) {
  const usedPct = pct(data.totalUsedMb, data.totalQuotaMb);

  return (
    <div className="space-y-6">

      {/* ── Platform totals ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Quota',      value: fmtMb(data.totalQuotaMb),   icon: 'ri-hard-drive-2-line',  color: 'text-blue-600 bg-blue-50' },
          { label: 'Total Used',       value: fmtMb(data.totalUsedMb),    icon: 'ri-database-line',       color: 'text-violet-600 bg-violet-50' },
          { label: 'Available',        value: fmtMb(Math.max(0, data.totalQuotaMb - data.totalUsedMb)), icon: 'ri-inbox-unarchive-line', color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Over Quota',       value: data.tenantsOverQuota,       icon: 'ri-error-warning-line',  color: data.tenantsOverQuota > 0 ? 'text-red-600 bg-red-50' : 'text-slate-500 bg-slate-100' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <span className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${s.color.split(' ')[1]}`}>
              <i className={`${s.icon} text-base ${s.color.split(' ')[0]}`}></i>
            </span>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">{s.label}</p>
              <p className="text-slate-900 text-xl font-bold">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Platform usage bar ── */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-slate-700 text-sm font-semibold mb-4">Platform Storage Usage</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600 font-medium">{fmtMb(data.totalUsedMb)} used</span>
              <span className="text-slate-400">of {fmtMb(data.totalQuotaMb)} allocated</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all ${barColor(usedPct)}`}
                style={{ width: `${usedPct}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{usedPct}% used</span>
              <span>{100 - usedPct}% free</span>
            </div>
          </div>

          {/* VPS status */}
          <div className="mt-5 pt-4 border-t border-slate-100">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-3">Storage Backend</p>
            <div className="space-y-2.5">
              {[
                { label: 'Supabase Storage (fallback)', level: 'active',   note: 'Small files · limited quota' },
                { label: 'VPS Object Storage',           level: 'prepared', note: 'Photo uploads · pending backend' },
                { label: 'CDN / Delivery',               level: 'pending',  note: 'Not configured' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-slate-700 text-xs font-medium">{item.label}</p>
                    <p className="text-slate-400 text-[10px]">{item.note}</p>
                  </div>
                  <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${
                    item.level === 'active'   ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    item.level === 'prepared' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-slate-100 text-slate-500 border-slate-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      item.level === 'active' ? 'bg-emerald-400 animate-pulse' : item.level === 'prepared' ? 'bg-amber-400' : 'bg-slate-300'
                    }`}></span>
                    {item.level === 'active' ? 'Active' : item.level === 'prepared' ? 'Prepared' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Top tenants by storage ── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-slate-700 text-sm font-semibold">Top Tenants by Usage</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {data.tenantStorageList.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <i className="ri-hard-drive-line text-slate-200 text-3xl mb-2"></i>
                <p className="text-slate-400 text-sm">No storage data yet</p>
              </div>
            ) : (
              data.tenantStorageList.map((t) => {
                const p = pct(t.usedMb, t.quotaMb);
                return (
                  <div key={t.id} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold shrink-0">
                          {t.name.charAt(0).toUpperCase()}
                        </div>
                        <p className="text-slate-700 text-xs font-medium truncate">{t.name}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-slate-500 text-xs">{fmtMb(t.usedMb)}</span>
                        <span className="text-slate-300 text-xs">/ {fmtMb(t.quotaMb)}</span>
                        <span className={`text-xs font-bold ${p > 85 ? 'text-red-500' : p > 60 ? 'text-amber-500' : 'text-slate-400'}`}>
                          {p}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                      <div className={`h-1.5 rounded-full ${barColor(p)}`} style={{ width: `${p}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Warning if over quota ── */}
      {data.tenantsOverQuota > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg shrink-0 mt-0.5"></i>
          <div>
            <p className="text-red-900 text-sm font-semibold">
              {data.tenantsOverQuota} tenant{data.tenantsOverQuota > 1 ? 's' : ''} over quota
            </p>
            <p className="text-red-600 text-xs mt-1">
              Some tenants are exceeding their storage limits. Review in the Tenants section and consider upgrading their plan.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
